import React, { useEffect, useState, useRef } from "react";
import axios from "axios";
import { MyDomain } from "../../App";
import { useParams, useHistory } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import { useTranslation } from "react-i18next";

delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
});

// ─── Design Tokens ────────────────────────────────────────────────────────────
const C = {
  bg:          "#F4F5F7",
  surface:     "#FFFFFF",
  primary:     "#1A1A2E",
  accent:      "#C9A84C",
  accentLight: "#FDF6E3",
  text:        "#1A1A2E",
  textSub:     "#64748B",
  textMuted:   "#A0AEC0",
  border:      "#E8ECF0",
  star:        "#F59E0B",
  starEmpty:   "#E5E7EB",
  green:       "#10B981",
  blue:        "#3B82F6",
  red:         "#EF4444",
};

// ─── Map updater ──────────────────────────────────────────────────────────────
function MapUpdater({ center, zoom }) {
  const map = useMap();
  useEffect(() => {
    if (center && !isNaN(center[0]) && !isNaN(center[1]))
      map.setView(center, zoom);
  }, [center, zoom, map]);
  return null;
}

// ─── Star row ─────────────────────────────────────────────────────────────────
function Stars({ rating, size = 13 }) {
  return (
    <div style={{ display: "flex", gap: 2 }}>
      {[1, 2, 3, 4, 5].map((s) => (
        <svg key={s} width={size} height={size} viewBox="0 0 24 24"
          fill={s <= rating ? C.star : C.starEmpty}
          stroke={s <= rating ? "#E07B00" : "#D1D5DB"} strokeWidth="1">
          <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
        </svg>
      ))}
    </div>
  );
}

// ─── Placeholder images ───────────────────────────────────────────────────────
function ImgPlaceholder({ type = "main" }) {
  const shared = { width: "100%", height: "100%", display: "flex",
    alignItems: "center", justifyContent: "center" };
  if (type === "main")
    return (
      <div style={{ ...shared, flexDirection: "column", gap: 8,
        background: "linear-gradient(135deg,#F0F2F5 0%,#E8EAED 100%)" }}>
        <span style={{ fontSize: 36, opacity: 0.3 }}>✂️</span>
        <span style={{ fontSize: 11, color: C.textMuted, fontWeight: 500 }}>No Image</span>
      </div>
    );
  if (type === "side")
    return <div style={{ ...shared, background: "#EAECEF" }}>
      <span style={{ fontSize: 20, opacity: 0.3 }}>📷</span>
    </div>;
  return (
    <div style={{ ...shared, borderRadius: "50%", background: C.accentLight,
      border: `2px solid ${C.accent}66` }}>
      <span style={{ fontSize: 22 }}>✂️</span>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────
export default function AllCategories({ CategoryName }) {
  const { t } = useTranslation();
  const params  = useParams();
  const history = useHistory();
  const category = CategoryName || params.CategoryName;

  const [barbers,      setBarbers]      = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [addresses,    setAddresses]    = useState({});
  const [barberImages, setBarberImages] = useState({});
  const [barberRatings,setBarberRatings]= useState({});
  const [currentPage,  setCurrentPage]  = useState(1);
  const [selectedBarber,setSelectedBarber]=useState(null);
  const [mapCenter,    setMapCenter]    = useState([62.0, 15.0]);
  const [mapZoom,      setMapZoom]      = useState(5);
  const [windowWidth,  setWindowWidth]  = useState(window.innerWidth);
  const [searchTerm,   setSearchTerm]   = useState("");
  const [ratingFilter, setRatingFilter] = useState("all");
  const [userLocation, setUserLocation] = useState(null);
  const [showNearbyOnly,setShowNearbyOnly]=useState(false);
  const [locationLoading,setLocationLoading]=useState(false);
  const [showRatingDrop, setShowRatingDrop]=useState(false);

  const itemsPerPage = 10;
  const listRef = useRef(null);

  useEffect(() => {
    const onResize = () => setWindowWidth(window.innerWidth);
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  useEffect(() => { if (category === "haircut") fetchBarbers(); }, [category]);

  useEffect(() => {
    if (!barbers.length) return;
    const valid = barbers.filter(b =>
      b.location?.lat && b.location?.long &&
      !isNaN(+b.location.lat) && !isNaN(+b.location.long));
    if (!valid.length) return;
    const lat = valid.reduce((s, b) => s + +b.location.lat,  0) / valid.length;
    const lng = valid.reduce((s, b) => s + +b.location.long, 0) / valid.length;
    if (!isNaN(lat) && !isNaN(lng)) setMapCenter([lat, lng]);
  }, [barbers]);

  useEffect(() => { setCurrentPage(1); }, [searchTerm, ratingFilter, showNearbyOnly]);

  // ─── Data fetchers ────────────────────────────────────────────────────────
  const fetchBarbers = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${MyDomain}Barbers2/GetBarbersse`);
      setBarbers(data);
      data.forEach(item => {
        if (item.location?.lat && item.location?.long)
          getAddress(item.barber.id, item.location.lat, item.location.long);
        fetchImages(item.barber.id);
        fetchReviews(item.barber.phoneNumber);
      });
    } catch (e) { console.error(e); }
    finally { setLoading(false); }
  };

  const fetchImages = async (id) => {
    try {
      const { data } = await axios.get(`${MyDomain}Barbers2/GetAllImagesByBarberIdse/${id}`);
      setBarberImages(p => ({ ...p, [id]: {
        img1: data.mainImage, img2: data.mainImage2, img3: data.mainImage3 }}));
    } catch (e) {}
  };

  const fetchReviews = async (phone) => {
    try {
      const { data } = await axios.post(`${MyDomain}Barbers2/GetAllBarberDatase`, { phoneNumber: phone });
      const reviews = data.allReviews || [];
      if (!reviews.length) return;
      const avg = (reviews.reduce((a, r) => a + r.rating, 0) / reviews.length).toFixed(1);
      setBarberRatings(p => ({ ...p, [phone]: { average: avg, count: reviews.length } }));
    } catch (e) {}
  };

  const getAddress = async (id, lat, lng) => {
    try {
      const r = await fetch(
        `https://maps.googleapis.com/maps/api/geocode/json?latlng=${lat},${lng}&key=AIzaSyA4HcCM2bP0v8Chi2bdFL7vAhCVkepKsBY`);
      const d = await r.json();
      if (d.results[0]) {
        const cs = d.results[0].address_components;
        const street = cs.find(c => c.types.includes("route"))?.long_name || "";
        const city   = cs.find(c => c.types.includes("locality"))?.long_name || "";
        setAddresses(p => ({ ...p, [id]: street && city ? `${street}, ${city}` : d.results[0].formatted_address }));
      }
    } catch (e) {}
  };

  // ─── Geo ──────────────────────────────────────────────────────────────────
  const dist = (la1, lo1, la2, lo2) => {
    const R = 6371, dLa = (la2-la1)*Math.PI/180, dLo = (lo2-lo1)*Math.PI/180;
    const a = Math.sin(dLa/2)**2 + Math.cos(la1*Math.PI/180)*Math.cos(la2*Math.PI/180)*Math.sin(dLo/2)**2;
    return R*2*Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  };

  const getUserLocation = () => {
    if (!navigator.geolocation) return alert("Geolocation not supported.");
    setLocationLoading(true);
    navigator.geolocation.getCurrentPosition(
      pos => {
        const { latitude: la, longitude: lo } = pos.coords;
        setUserLocation({ lat: la, lng: lo });
        setShowNearbyOnly(true);
        setMapCenter([la, lo]);
        setMapZoom(13);
        setLocationLoading(false);
      },
      () => { alert("Unable to get location."); setLocationLoading(false); },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  const clearLocation = () => {
    setUserLocation(null);
    setShowNearbyOnly(false);
    const valid = barbers.filter(b => b.location?.lat && !isNaN(+b.location.lat));
    if (valid.length) {
      const la = valid.reduce((s,b)=>s + +b.location.lat,0)/valid.length;
      const lo = valid.reduce((s,b)=>s + +b.location.long,0)/valid.length;
      setMapCenter([la, lo]); setMapZoom(6);
    }
  };

  // ─── Filtering ────────────────────────────────────────────────────────────
  const getFiltered = () => {
    let f = barbers;
    if (searchTerm.trim()) {
      const s = searchTerm.toLowerCase();
      f = f.filter(b => b.barber.name?.toLowerCase().includes(s) ||
                        b.barber.phoneNumber?.toLowerCase().includes(s));
    }
    if (ratingFilter !== "all") {
      f = f.filter(b => {
        const r = barberRatings[b.barber.phoneNumber];
        if (!r) return false;
        const a = +r.average;
        if (ratingFilter === "5") return a >= 4.5;
        if (ratingFilter === "4") return a >= 4 && a < 4.5;
        if (ratingFilter === "3") return a >= 3 && a < 4;
        if (ratingFilter === "below3") return a < 3;
        return true;
      });
    }
    if (showNearbyOnly && userLocation) {
      f = f.filter(b => {
        if (!b.location?.lat) return false;
        return dist(userLocation.lat, userLocation.lng, +b.location.lat, +b.location.long) <= 15;
      });
    }
    return f;
  };

  const filtered      = getFiltered();
  const totalPages    = Math.ceil(filtered.length / itemsPerPage);
  const pageBarbers   = filtered.slice((currentPage-1)*itemsPerPage, currentPage*itemsPerPage);
  const mapBarbers    = filtered.filter(b => b.location?.lat && !isNaN(+b.location.lat));
  const hasFilters    = searchTerm || ratingFilter !== "all" || showNearbyOnly;
  const isDesktop     = windowWidth > 1024;
  const isMobile      = windowWidth <= 640;

  const changePage = (p) => {
    setCurrentPage(p);
    if (isDesktop && listRef.current) listRef.current.scrollTo({ top: 0, behavior: "smooth" });
    else window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const clearAll = () => { setSearchTerm(""); setRatingFilter("all"); if (showNearbyOnly) clearLocation(); };

  const ratingOptions = [
    { value: "all",     label: t("AllRatings") || "All Ratings" },
    { value: "5",       label: "★ 4.5 – 5.0" },
    { value: "4",       label: "★ 4.0 – 4.4" },
    { value: "3",       label: "★ 3.0 – 3.9" },
    { value: "below3",  label: `★ ${t("Below") || "Below"} 3.0` },
  ];

  // ─── Render ───────────────────────────────────────────────────────────────
  return (
    <div style={{ minHeight: "100vh", backgroundColor: C.bg, fontFamily: "'Segoe UI', sans-serif" }}>
      <style>{`
        @keyframes spin { to { transform: rotate(360deg); } }
        @keyframes fadeIn { from { opacity:0; transform:translateY(6px); } to { opacity:1; transform:translateY(0); } }
        .ac-card { animation: fadeIn 0.25s ease both; }
        .ac-card:hover { transform: translateY(-3px) !important; box-shadow: 0 12px 32px rgba(0,0,0,0.13) !important; }
        .ac-book:hover { background: ${C.accent} !important; color: ${C.primary} !important; }
        .ac-pill:hover { border-color: ${C.accent} !important; color: ${C.accent} !important; }
        ::-webkit-scrollbar { width: 6px; }
        ::-webkit-scrollbar-thumb { background: #D1D5DB; border-radius: 3px; }
        .leaflet-container { font-family: 'Segoe UI', sans-serif; }
      `}</style>

      {/* ══════════════ HEADER ══════════════ */}
      <header style={{
        position: "sticky", top: 0, zIndex: 200,
        background: C.primary,
        boxShadow: "0 2px 16px rgba(0,0,0,0.25)",
      }}>
        <div style={{
          maxWidth: 1400, margin: "0 auto",
          padding: isMobile ? "12px 16px" : "14px 32px",
          display: "flex", alignItems: "center", gap: 12,
        }}>
          <button onClick={() => history.goBack()} style={{
            width: 38, height: 38, borderRadius: "50%", border: "none",
            background: "rgba(255,255,255,0.12)", cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center",
            flexShrink: 0, transition: "background 0.15s",
          }}
            onMouseEnter={e => e.currentTarget.style.background = "rgba(255,255,255,0.22)"}
            onMouseLeave={e => e.currentTarget.style.background = "rgba(255,255,255,0.12)"}
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="white" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
          </button>

          <div style={{ flex: 1, textAlign: "center" }}>
            <div style={{ fontSize: isMobile ? 15 : 18, fontWeight: 700, color: "#fff", letterSpacing: 0.3 }}>
              {t("SalonsTitle") || "Salons"}
            </div>
            <div style={{
              display: "inline-block", marginTop: 4,
              background: C.accent, color: C.primary,
              fontSize: 10, fontWeight: 800, letterSpacing: 1,
              padding: "2px 10px", borderRadius: 20, textTransform: "capitalize",
            }}>
              {category}
            </div>
          </div>

          <div style={{ width: 38 }} />
        </div>
      </header>

      {/* ══════════════ FILTER BAR ══════════════ */}
      <div style={{
        background: C.surface,
        borderBottom: `1px solid ${C.border}`,
        boxShadow: "0 2px 8px rgba(0,0,0,0.05)",
        position: "sticky", top: isDesktop ? 68 : 67, zIndex: 100,
      }}>
        <div style={{
          maxWidth: 1400, margin: "0 auto",
          padding: isMobile ? "10px 14px" : "12px 32px",
        }}>
          {/* Row 1: search + pills */}
          <div style={{
            display: "flex", gap: 10, alignItems: "center",
            flexWrap: isMobile ? "wrap" : "nowrap",
          }}>
            {/* Search */}
            <div style={{ position: "relative", flex: isMobile ? "1 1 100%" : "1 1 320px", maxWidth: isDesktop ? 400 : "none" }}>
              <svg style={{ position: "absolute", left: 12, top: "50%", transform: "translateY(-50%)",
                pointerEvents: "none", color: C.textMuted }}
                width="15" height="15" viewBox="0 0 24 24" fill="none"
                stroke={C.textMuted} strokeWidth="2" strokeLinecap="round">
                <circle cx="11" cy="11" r="8"/><path d="M21 21l-4.35-4.35"/>
              </svg>
              <input
                type="text"
                placeholder={t("SearchByNameOrPhoneNumber") || "Search by name or phone…"}
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                style={{
                  width: "100%", height: 40, padding: "0 38px 0 36px",
                  fontSize: 13, border: `1.5px solid ${C.border}`,
                  borderRadius: 10, background: C.bg, color: C.text,
                  outline: "none", boxSizing: "border-box", transition: "border-color 0.15s",
                }}
                onFocus={e => e.target.style.borderColor = C.accent}
                onBlur={e  => e.target.style.borderColor = C.border}
              />
              {searchTerm && (
                <button onClick={() => setSearchTerm("")} style={{
                  position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
                  width: 18, height: 18, borderRadius: "50%", border: "none",
                  background: C.textMuted, color: "#fff", cursor: "pointer",
                  fontSize: 10, display: "flex", alignItems: "center", justifyContent: "center",
                }}>✕</button>
              )}
            </div>

            {/* Pills row — scrollable on mobile */}
            <div style={{
              display: "flex", gap: 8, alignItems: "center",
              overflowX: "auto", scrollbarWidth: "none",
              flex: isMobile ? "1 1 100%" : "0 0 auto",
              paddingBottom: isMobile ? 2 : 0,
            }}>
              {/* Rating dropdown pill */}
              <div style={{ position: "relative", flexShrink: 0 }}>
                <button className="ac-pill" onClick={() => setShowRatingDrop(p => !p)} style={{
                  height: 36, padding: "0 14px", borderRadius: 20,
                  border: `1.5px solid ${ratingFilter !== "all" ? C.accent : C.border}`,
                  background: ratingFilter !== "all" ? C.accentLight : C.surface,
                  color: ratingFilter !== "all" ? C.accent : C.textSub,
                  fontSize: 13, fontWeight: ratingFilter !== "all" ? 700 : 500,
                  cursor: "pointer", display: "flex", alignItems: "center", gap: 5,
                  whiteSpace: "nowrap", transition: "all 0.15s",
                }}>
                  ★ {ratingOptions.find(o => o.value === ratingFilter)?.label}
                  <span style={{ fontSize: 9, color: C.textMuted }}>
                    {showRatingDrop ? "▲" : "▼"}
                  </span>
                </button>
                {showRatingDrop && (
                  <div style={{
                    position: "absolute", top: "calc(100% + 6px)", left: 0,
                    background: C.surface, border: `1px solid ${C.border}`,
                    borderRadius: 12, boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
                    zIndex: 300, overflow: "hidden", minWidth: 170,
                  }}>
                    {ratingOptions.map(o => (
                      <div key={o.value} onClick={() => { setRatingFilter(o.value); setShowRatingDrop(false); }}
                        style={{
                          padding: "10px 16px", fontSize: 13, cursor: "pointer",
                          color: ratingFilter === o.value ? C.accent : C.textSub,
                          background: ratingFilter === o.value ? C.accentLight : "transparent",
                          fontWeight: ratingFilter === o.value ? 700 : 400,
                          display: "flex", justifyContent: "space-between",
                          borderBottom: `1px solid ${C.border}`,
                          transition: "background 0.1s",
                        }}
                        onMouseEnter={e => { if (ratingFilter !== o.value) e.currentTarget.style.background = "#F8F9FA"; }}
                        onMouseLeave={e => { e.currentTarget.style.background = ratingFilter === o.value ? C.accentLight : "transparent"; }}
                      >
                        {o.label}
                        {ratingFilter === o.value && <span style={{ color: C.accent }}>✓</span>}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Nearby pill */}
              <button className="ac-pill" onClick={showNearbyOnly ? clearLocation : getUserLocation}
                disabled={locationLoading} style={{
                  height: 36, padding: "0 14px", borderRadius: 20,
                  border: `1.5px solid ${showNearbyOnly ? C.green : C.border}`,
                  background: showNearbyOnly ? "#ECFDF5" : C.surface,
                  color: showNearbyOnly ? C.green : C.textSub,
                  fontSize: 13, fontWeight: showNearbyOnly ? 700 : 500,
                  cursor: "pointer", display: "flex", alignItems: "center", gap: 6,
                  whiteSpace: "nowrap", transition: "all 0.15s", flexShrink: 0,
                }}>
                {locationLoading
                  ? <span style={{ width: 14, height: 14, border: `2px solid ${C.green}`,
                      borderTopColor: "transparent", borderRadius: "50%",
                      display: "inline-block", animation: "spin 0.7s linear infinite" }} />
                  : "📍"}
                {locationLoading
                  ? (t("GettingLocation") || "Getting location…")
                  : showNearbyOnly
                    ? `${t("CloseToMe") || "Near me"} ✓`
                    : (t("CloseToMe") || "Near me")}
              </button>

              {/* Clear all */}
              {hasFilters && (
                <button onClick={clearAll} style={{
                  height: 36, padding: "0 14px", borderRadius: 20,
                  border: `1.5px solid #FCA5A5`,
                  background: "#FEF2F2", color: C.red,
                  fontSize: 13, fontWeight: 600, cursor: "pointer",
                  display: "flex", alignItems: "center", gap: 5,
                  whiteSpace: "nowrap", transition: "all 0.15s", flexShrink: 0,
                }}>
                  ✕ {t("ClearAllFilters") || "Clear all"}
                </button>
              )}
            </div>
          </div>

          {/* Row 2: results count */}
          <div style={{ marginTop: 8, fontSize: 12, color: C.textMuted }}>
            <span style={{ fontWeight: 700, color: C.text }}>{filtered.length}</span>
            {" "}
            <span>{t("SalonsFor") || "salons for"}</span>
            {" "}
            <span style={{ color: C.accent, fontWeight: 700, textTransform: "capitalize" }}>{category}</span>
          </div>
        </div>
      </div>

      {/* ══════════════ BODY ══════════════ */}
      <div style={{ maxWidth: 1400, margin: "0 auto", padding: isMobile ? "14px" : "24px 32px" }}>
        {category === "haircut" && (
          loading ? (
            /* Loading */
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center",
              justifyContent: "center", minHeight: 420, gap: 18,
            }}>
              <div style={{
                width: 80, height: 80, borderRadius: "50%",
                background: C.accentLight, border: `2px solid ${C.accent}44`,
                display: "flex", alignItems: "center", justifyContent: "center",
              }}>
                <span style={{ fontSize: 34 }}>✂️</span>
              </div>
              <div style={{
                width: 44, height: 44, border: `3px solid ${C.border}`,
                borderTop: `3px solid ${C.accent}`, borderRadius: "50%",
                animation: "spin 0.9s linear infinite",
              }} />
              <div>
                <div style={{ fontSize: 17, fontWeight: 700, color: C.text, textAlign: "center" }}>
                  {t("LoadingSalons") || "Loading Salons…"}
                </div>
                <div style={{ fontSize: 13, color: C.textMuted, marginTop: 4, textAlign: "center" }}>
                  {t("LoadingSalonsSubtitle") || "Finding the best barbers for you"}
                </div>
              </div>
            </div>
          ) : (
            /* Content */
            <div style={{ display: "flex", gap: 24, alignItems: "flex-start" }}>

              {/* ── List column ── */}
              <div style={{
                flex: isDesktop ? "0 0 calc(55% - 12px)" : "1",
                display: "flex", flexDirection: "column",
              }}>
                <div
                  ref={listRef}
                  style={{
                    maxHeight: isDesktop ? "calc(100vh - 220px)" : "none",
                    overflowY: isDesktop ? "auto" : "visible",
                    paddingRight: isDesktop ? 6 : 0,
                  }}
                >
                  {pageBarbers.length === 0 ? (
                    <div style={{
                      textAlign: "center", padding: "64px 24px",
                      background: C.surface, borderRadius: 20,
                      border: `1px solid ${C.border}`,
                    }}>
                      <div style={{ fontSize: 52, opacity: 0.2, marginBottom: 12 }}>✂️</div>
                      <div style={{ fontSize: 17, fontWeight: 700, color: C.text }}>
                        {t("NoBarbersFound") || "No barbers found"}
                      </div>
                      <div style={{ fontSize: 13, color: C.textMuted, marginTop: 6 }}>
                        {t("TryAdjustingFilters") || "Try adjusting your filters"}
                      </div>
                    </div>
                  ) : (
                    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                      {pageBarbers.map((item, idx) => {
                        const imgs   = barberImages[item.barber.id] || {};
                        const img1   = imgs.img1?.image || item.barber.image;
                        const img2   = imgs.img2?.image;
                        const img3   = imgs.img3?.image;
                        const rating = barberRatings[item.barber.phoneNumber];
                        const rNum   = rating ? +rating.average : 0;
                        const isSelected = selectedBarber === item.barber.id;

                        return (
                          <div key={item.barber.id || idx}
                            className="ac-card"
                            style={{ animationDelay: `${idx * 40}ms` }}
                            onMouseEnter={() => {
                              if (isDesktop && item.location?.lat) {
                                setSelectedBarber(item.barber.id);
                                setMapCenter([+item.location.lat, +item.location.long]);
                                setMapZoom(13);
                              }
                            }}
                            onMouseLeave={() => { setSelectedBarber(null); setMapZoom(8); }}
                            onClick={() => {
                              history.push(`/barber-main-page/${item.barber.phoneNumber}`);
                              window.scrollTo({ top: 0, behavior: "smooth" });
                            }}
                          >
                            <div style={{
                              background: C.surface,
                              borderRadius: 18,
                              overflow: "hidden",
                              border: isSelected
                                ? `2px solid ${C.accent}`
                                : `1px solid ${C.border}`,
                              boxShadow: isSelected
                                ? `0 8px 28px rgba(201,168,76,0.22)`
                                : "0 2px 10px rgba(0,0,0,0.07)",
                              cursor: "pointer",
                              transition: "all 0.22s ease",
                            }}>
                              {/* Image grid */}
                              <div style={{
                                height: isMobile ? 160 : 210,
                                display: "grid",
                                gridTemplateColumns: "2fr 1fr",
                                gridTemplateRows: "1fr 1fr",
                                gap: 3,
                                background: "#EAECEF",
                              }}>
                                {/* Main image */}
                                <div style={{ gridRow: "1 / 3", overflow: "hidden", position: "relative" }}>
                                  {img1
                                    ? <img src={img1} alt={item.barber.name}
                                        style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                    : <ImgPlaceholder type="main" />
                                  }
                                  {rating && (
                                    <div style={{
                                      position: "absolute", bottom: 10, left: 10,
                                      background: "rgba(0,0,0,0.58)",
                                      borderRadius: 8, padding: "3px 8px",
                                      display: "flex", alignItems: "center", gap: 4,
                                    }}>
                                      <span style={{ color: C.star, fontSize: 12 }}>★</span>
                                      <span style={{ color: "#fff", fontSize: 12, fontWeight: 700 }}>
                                        {rating.average}
                                      </span>
                                    </div>
                                  )}
                                </div>
                                {/* Side images — hidden on mobile */}
                                {!isMobile && (
                                  <>
                                    <div style={{ overflow: "hidden" }}>
                                      {img2 ? <img src={img2} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                             : <ImgPlaceholder type="side" />}
                                    </div>
                                    <div style={{ overflow: "hidden" }}>
                                      {img3 ? <img src={img3} alt="" style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                             : <ImgPlaceholder type="side" />}
                                    </div>
                                  </>
                                )}
                              </div>

                              {/* Info */}
                              <div style={{ padding: isMobile ? "12px 14px" : "16px 20px" }}>
                                {/* Profile row */}
                                <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
                                  <div style={{
                                    width: isMobile ? 42 : 50, height: isMobile ? 42 : 50,
                                    borderRadius: "50%", flexShrink: 0, overflow: "hidden",
                                    border: `2px solid ${C.accent}`,
                                    boxShadow: `0 2px 8px ${C.accent}44`,
                                  }}>
                                    {item.barber.image
                                      ? <img src={item.barber.image} alt={item.barber.name}
                                          style={{ width: "100%", height: "100%", objectFit: "cover" }} />
                                      : <ImgPlaceholder type="profile" />
                                    }
                                  </div>
                                  <div style={{ flex: 1, minWidth: 0 }}>
                                    <div style={{
                                      fontSize: isMobile ? 14 : 16, fontWeight: 700, color: C.text,
                                      whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                    }}>
                                      {item.barber.name}
                                    </div>
                                    {!isMobile && (
                                      <div style={{ fontSize: 12, color: C.textMuted, marginTop: 2 }}>
                                        {item.barber.phoneNumber}
                                      </div>
                                    )}
                                  </div>
                                  <button className="ac-book" style={{
                                    padding: "7px 16px", borderRadius: 10, border: "none",
                                    background: C.primary, color: C.accent,
                                    fontSize: 12, fontWeight: 700, cursor: "pointer",
                                    letterSpacing: 0.4, transition: "all 0.15s", flexShrink: 0,
                                  }}>
                                    {t("Book") || "Book"}
                                  </button>
                                </div>

                                <div style={{ height: 1, background: C.border, marginBottom: 10 }} />

                                {/* Address + rating */}
                                <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                                  {addresses[item.barber.id] && (
                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                      <span style={{ fontSize: 13 }}>📍</span>
                                      <span style={{
                                        fontSize: 12, color: C.textSub,
                                        whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis",
                                      }}>
                                        {addresses[item.barber.id]}
                                      </span>
                                    </div>
                                  )}
                                  {rating && (
                                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                      <Stars rating={Math.round(rNum)} />
                                      <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>
                                        {rating.average}
                                      </span>
                                      <span style={{ fontSize: 11, color: C.textMuted }}>
                                        ({rating.count} {rating.count === 1
                                          ? (t("Review") || "review")
                                          : (t("ReviewsPlural") || "reviews")})
                                      </span>
                                    </div>
                                  )}
                                </div>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Pagination */}
                  {totalPages > 1 && (
                    <div style={{
                      display: "flex", alignItems: "center", justifyContent: "center",
                      gap: 6, padding: "20px 0 8px", flexWrap: "wrap",
                    }}>
                      <button onClick={() => currentPage > 1 && changePage(currentPage - 1)}
                        disabled={currentPage === 1} style={{
                          padding: "8px 18px", borderRadius: 10, border: "none",
                          background: currentPage === 1 ? C.border : C.primary,
                          color: currentPage === 1 ? C.textMuted : "#fff",
                          fontSize: 13, fontWeight: 600,
                          cursor: currentPage === 1 ? "not-allowed" : "pointer",
                        }}>
                        ← {t("Previous") || "Prev"}
                      </button>

                      {Array.from({ length: Math.min(totalPages, isMobile ? 5 : 8) }, (_, i) => {
                        const max = isMobile ? 5 : 8;
                        let p;
                        if (totalPages <= max)         p = i + 1;
                        else if (currentPage <= Math.floor(max/2)) p = i + 1;
                        else if (currentPage >= totalPages - Math.floor(max/2)) p = totalPages - max + i + 1;
                        else p = currentPage - Math.floor(max/2) + i;
                        return (
                          <button key={p} onClick={() => changePage(p)} style={{
                            width: 36, height: 36, borderRadius: 10,
                            border: `1.5px solid ${currentPage === p ? C.accent : C.border}`,
                            background: currentPage === p ? C.accent : C.surface,
                            color: currentPage === p ? C.primary : C.textSub,
                            fontSize: 13, fontWeight: currentPage === p ? 700 : 500,
                            cursor: "pointer", transition: "all 0.15s",
                          }}>
                            {p}
                          </button>
                        );
                      })}

                      <button onClick={() => currentPage < totalPages && changePage(currentPage + 1)}
                        disabled={currentPage === totalPages} style={{
                          padding: "8px 18px", borderRadius: 10, border: "none",
                          background: currentPage === totalPages ? C.border : C.primary,
                          color: currentPage === totalPages ? C.textMuted : "#fff",
                          fontSize: 13, fontWeight: 600,
                          cursor: currentPage === totalPages ? "not-allowed" : "pointer",
                        }}>
                        {t("Next") || "Next"} →
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* ── Map column (desktop only) ── */}
              {isDesktop && (
                <div style={{
                  flex: "0 0 calc(45% - 12px)",
                  position: "sticky", top: 136,
                  height: "calc(100vh - 160px)",
                  borderRadius: 18, overflow: "hidden",
                  border: `1px solid ${C.border}`,
                  boxShadow: "0 4px 20px rgba(0,0,0,0.10)",
                }}>
                  <MapContainer center={mapCenter} zoom={mapZoom}
                    style={{ width: "100%", height: "100%" }}>
                    <TileLayer
                      attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
                      url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                    />
                    <MapUpdater center={mapCenter} zoom={mapZoom} />

                    {userLocation && showNearbyOnly && (
                      <Marker position={[userLocation.lat, userLocation.lng]}
                        icon={L.divIcon({
                          className: "",
                          html: `<div style="background:${C.blue};width:18px;height:18px;border-radius:50%;border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3)"></div>`,
                          iconSize: [18, 18], iconAnchor: [9, 9],
                        })}>
                        <Popup>
                          <strong>📍 Your Location</strong>
                          <p style={{ margin: "4px 0 0", fontSize: 12 }}>Showing barbers within 15 km</p>
                        </Popup>
                      </Marker>
                    )}

                    {mapBarbers.map((item, i) => (
                      <Marker key={item.barber.id || i}
                        position={[+item.location.lat, +item.location.long]}
                        icon={selectedBarber === item.barber.id
                          ? L.divIcon({
                              className: "",
                              html: `<div style="background:${C.accent};width:22px;height:22px;border-radius:50%;border:3px solid white;box-shadow:0 2px 10px rgba(201,168,76,0.6)"></div>`,
                              iconSize: [22, 22], iconAnchor: [11, 11],
                            })
                          : new L.Icon.Default()
                        }>
                        <Popup>
                          <strong>{item.barber.name}</strong>
                          {addresses[item.barber.id] && (
                            <p style={{ margin: "4px 0 0", fontSize: 12 }}>📍 {addresses[item.barber.id]}</p>
                          )}
                        </Popup>
                      </Marker>
                    ))}
                  </MapContainer>
                </div>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
}
